import React from 'react';
import PropTypes from 'prop-types';
import {
  LuArrowLeftRight,
  LuBoxes,
  LuBuilding2,
  LuCalendarClock,
  LuCalendarDays,
  LuCoins,
  LuFileText,
  LuFolderKanban,
  LuHandshake,
  LuHistory,
  LuMapPin,
  LuPackage,
  LuPackageMinus,
  LuPencil,
  LuPercent,
  LuTag,
  LuToggleLeft,
  LuToggleRight,
  LuTrendingDown,
  LuUserPlus,
  LuWallet,
} from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import DetailPane from '../../../components/data/DetailPane';
import useStockText from '../stockText';
import { isEmptyValue } from '../stockColumns';
import { CATEGORY_GRADIENTS, CategoryBadge, StockStatusBadge, categoryKey, nameInitials } from './stockBits';

const text = (value) => (isEmptyValue(value) ? '' : String(value));

/**
 * The selected stock's panel: identity, the actions of the old action bar
 * (edit, assign, movements, category switch), every field of the card and the
 * status switch. Docked beside the table on wide screens, a drawer otherwise.
 */
const StockDetailPane = ({
  docked, height, stock, low, position, onPrev, onNext, onClose,
  onEdit, onAssign, onHistory, onToggleCategory, onToggleStatus,
}) => {
  const { label, ownershipLabel, formatDay } = useStockText();
  const paneLabel = label('panel.label');

  if (!stock) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const isActive = stock['Durum'] === 'aktif';
  const isSarf = stock['Kategori'] === 'sarf';
  const isFixture = stock['Kategori'] === 'demirbas';
  const currency = text(stock['Döviz Tipi']);
  const withCurrency = (value) => (text(value) && currency ? `${text(value)} ${currency}` : text(value));

  const badges = (
    <>
      <CategoryBadge value={text(stock['Kategori'])} />
      <StockStatusBadge value={text(stock['Durum']) || undefined} />
      {low && (
        <Badge tone="danger">
          <LuTrendingDown aria-hidden="true" className="tw-h-3 tw-w-3" />
          {label('panel.low')}
        </Badge>
      )}
    </>
  );

  const actions = [
    { key: 'edit', label: label('panel.edit'), icon: LuPencil, tint: 'blue', onClick: onEdit },
    { key: 'assign', label: label('panel.assign'), icon: LuUserPlus, tint: 'teal', onClick: onAssign },
    { key: 'history', label: label('panel.history'), icon: LuHistory, tint: 'violet', onClick: onHistory },
    {
      key: 'category',
      label: isSarf ? label('panel.toDemirbas') : label('panel.toSarf'),
      icon: LuArrowLeftRight,
      tint: 'amber',
      title: label('panel.categoryHint'),
      onClick: onToggleCategory,
    },
  ];

  const details = [
    { key: 'type', icon: LuTag, label: label('columns.stockType'), value: text(stock['Stok Tipi']) },
    { key: 'info', icon: LuFileText, label: label('columns.info'), value: text(stock['Bilgi']) },
    { key: 'customer', icon: LuBuilding2, label: label('columns.customer'), value: text(stock.customerName) },
    { key: 'project', icon: LuFolderKanban, label: label('columns.project'), value: text(stock.projectName) },
    ...(isFixture ? [{ key: 'ownership', icon: LuHandshake, label: label('columns.ownership'), value: ownershipLabel(stock.ownershipType) }] : []),
    { key: 'warehouse', icon: LuMapPin, label: label('columns.warehouse'), value: text(stock['Depo']) },
    { key: 'quantity', icon: LuPackage, label: label('columns.quantity'), value: text(stock['Miktar']) },
    { key: 'remaining', icon: LuPackageMinus, label: label('columns.remaining'), value: text(stock['Kalan']) },
    { key: 'min', icon: LuTrendingDown, label: label('columns.minQuantity'), value: text(stock['Min Miktar']) },
    { key: 'purchase', icon: LuWallet, label: label('columns.purchasePrice'), value: withCurrency(stock['Giriş Fiyatı']) },
    { key: 'sale', icon: LuCoins, label: label('columns.salePrice'), value: withCurrency(stock['Çıkış Fiyatı']) },
    { key: 'rate', icon: LuCoins, label: label('columns.rate'), value: text(stock['Döviz Kuru']) },
    { key: 'profit', icon: LuPercent, label: label('columns.profitRate'), value: text(stock['Kar Oranı(%)']) ? `%${text(stock['Kar Oranı(%)'])}` : '' },
    ...(isFixture ? [{ key: 'depreciation', icon: LuPercent, label: label('columns.depreciation'), value: text(stock['Yıpranma Payı']) }] : []),
    { key: 'start', icon: LuCalendarDays, label: label('columns.startDate'), value: formatDay(stock['Kullanma Başlama Tarihi']) },
    { key: 'expiry', icon: LuCalendarClock, label: label('columns.expiryDate'), value: formatDay(stock['Son Kullanma Tarihi']) },
  ];

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={stock.id}
      identity={{
        avatar: isEmptyValue(stock['Adı']) ? <LuBoxes aria-hidden="true" className="tw-h-7 tw-w-7" /> : nameInitials(stock['Adı']),
        gradient: CATEGORY_GRADIENTS[categoryKey(stock['Kategori'])],
        title: text(stock['Adı']) || '-',
        subtitle: text(stock['Stok Tipi']) || undefined,
        badges,
      }}
      actions={actions}
      details={details}
      footer={{
        label: isActive ? label('panel.deactivate') : label('panel.activate'),
        icon: isActive ? LuToggleLeft : LuToggleRight,
        tone: isActive ? 'danger' : 'success',
        title: label('panel.statusHint'),
        onClick: onToggleStatus,
      }}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    />
  );
};

StockDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  stock: PropTypes.object,
  low: PropTypes.bool,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
  onHistory: PropTypes.func.isRequired,
  onToggleCategory: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
};

export default StockDetailPane;
